// Iter 383 (Renzo) — Historial de nóminas TEF de colaboradores (manual y desde Excel) con re-descarga del archivo.
// Iter 395 — estado del Comprobante de Egreso en Alegra por nómina: Crear (reintento) / Anular.
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Download, History, Loader2, FileSpreadsheet, FileText, Landmark, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { descargarTxtB64 } from './NominaExcelDialog';
import { EgresoAlegraNominaFields, egresoCfgInicial } from './EgresoAlegraNominaFields';

const CLP = (v) => Math.round(v || 0).toLocaleString('es-CL');
const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false }) : '');

function EstadoEgreso({ n }) {
  const e = n.egreso_alegra;
  if ((n.motivo || '').toUpperCase() === 'CTN') return <span className="text-[11px] text-muted-foreground">CTN · no aplica</span>;
  if (!e) return <span className="text-[11px] text-muted-foreground" data-testid={`egreso-estado-${n.id}`}>Sin egreso</span>;
  if (e.estado === 'creado') {
    return (
      <span className="text-[11px] text-emerald-700" data-testid={`egreso-estado-${n.id}`} title={`${e.cuenta_nombre || ''} · ${e.fecha || ''}`}>
        <Landmark className="inline h-3 w-3 mr-0.5" />N°{e.number || e.payment_id}<span className="block text-muted-foreground truncate max-w-[150px]">{e.cuenta_nombre}</span>
      </span>
    );
  }
  if (e.estado === 'anulado') return <span className="text-[11px] text-slate-500 line-through" data-testid={`egreso-estado-${n.id}`}>N°{e.number || e.payment_id} anulado</span>;
  return <span className="text-[11px] text-red-700" data-testid={`egreso-estado-${n.id}`} title={e.error}>⚠ Error: <span className="block max-w-[170px] truncate">{e.error}</span></span>;
}

export function NominasHistorialDialog({ open, onClose, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bajando, setBajando] = useState(null);
  const [crearPara, setCrearPara] = useState(null);
  const [anularPara, setAnularPara] = useState(null);
  const [egresoCfg, setEgresoCfg] = useState(egresoCfgInicial());
  const [procesando, setProcesando] = useState(false);

  const cargar = () => {
    setLoading(true);
    api.get('/tef/cuentas-colaboradores/nominas-historial', { params: { limit: 50 } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Error cargando historial'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) cargar(); }, [open, refreshKey]);

  const descargar = async (n) => {
    setBajando(n.id);
    try {
      const r = await api.get(`/tef/cuentas-colaboradores/nominas-historial/${n.id}/archivo`);
      descargarTxtB64(r.data.archivo);
      if (r.data.regenerado) toast.warning('Nómina anterior sin archivo guardado: se regeneró con los datos bancarios ACTUALES de los trabajadores', { duration: 8000 });
      else toast.success(`${r.data.archivo.filename} descargado`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo descargar el archivo'); }
    finally { setBajando(null); }
  };

  const abrirCrear = (n) => {
    setEgresoCfg({ crear: true, cuenta_id: n.egreso_alegra?.cuenta_id || '', fecha: n.egreso_alegra?.fecha || n.generada_at?.slice(0, 10) || '' });
    setCrearPara(n);
  };

  const crear = async () => {
    if (!crearPara) return;
    setProcesando(true);
    try {
      const r = await api.post(`/tef/cuentas-colaboradores/nominas-historial/${crearPara.id}/egreso-alegra`, { crear: true, cuenta_id: egresoCfg.cuenta_id, fecha: egresoCfg.fecha });
      toast.success(`Comprobante de Egreso N°${r.data.number || r.data.payment_id} creado en Alegra · $${CLP(r.data.monto)} · ${r.data.cuenta_nombre}`, { duration: 8000 });
      setCrearPara(null);
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo crear el egreso en Alegra', { duration: 10000 }); }
    finally { setProcesando(false); }
  };

  const anular = async () => {
    if (!anularPara) return;
    setProcesando(true);
    try {
      const r = await api.post(`/tef/cuentas-colaboradores/nominas-historial/${anularPara.id}/egreso-alegra/anular`);
      toast.success(`Egreso N°${r.data.number || r.data.payment_id} anulado en Alegra`);
      setAnularPara(null);
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo anular el egreso', { duration: 10000 }); }
    finally { setProcesando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] xl:max-w-7xl" data-testid="dialog-historial-nominas">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-slate-600" /> Historial de nóminas TEF · Colaboradores</DialogTitle>
          <DialogDescription>Re-descarga del archivo y Comprobante de Egreso en Alegra por nómina (crear / reintentar / anular si el TEF se descartó).</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>
        ) : items.length === 0 ? (
          <p className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="historial-empty">Aún no se han generado nóminas.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80">
                <tr>
                  <th className="px-3 py-1.5 text-left">Fecha</th>
                  <th className="px-3 py-1.5 text-left">Origen</th>
                  <th className="px-3 py-1.5 text-left">Archivo TEF</th>
                  <th className="px-3 py-1.5 text-left">Motivo</th>
                  <th className="px-3 py-1.5 text-right">Transf.</th>
                  <th className="px-3 py-1.5 text-right">Total</th>
                  <th className="px-3 py-1.5 text-left">Egreso Alegra</th>
                  <th className="px-3 py-1.5 text-left">Generada por</th>
                  <th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {items.map((n) => {
                  const esCTN = (n.motivo || '').toUpperCase() === 'CTN';
                  const vigente = n.egreso_alegra?.estado === 'creado';
                  return (
                    <tr key={n.id} className="border-t align-top" data-testid={`historial-fila-${n.id}`}>
                      <td className="whitespace-nowrap px-3 py-1.5">{fmtFecha(n.generada_at)} <span className="text-muted-foreground">{hora(n.generada_at)}</span></td>
                      <td className="px-3 py-1.5">
                        {n.origen === 'excel' ? (
                          <span className="flex items-center gap-1 text-emerald-700" title={n.archivo_origen || ''}><FileSpreadsheet className="h-3.5 w-3.5" /> Excel{n.archivo_origen ? <span className="max-w-[120px] truncate text-muted-foreground">· {n.archivo_origen}</span> : null}</span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-700"><FileText className="h-3.5 w-3.5" /> Manual</span>
                        )}
                      </td>
                      <td className="max-w-[190px] truncate px-3 py-1.5 font-mono text-[11px]" title={n.archivo_tef || ''}>{n.archivo_tef || <span className="italic text-muted-foreground">NominaColaboradores_*.TXT</span>}</td>
                      <td className="max-w-[160px] truncate px-3 py-1.5" title={n.motivo}>{n.motivo}</td>
                      <td className="px-3 py-1.5 text-right">{n.num_transacciones}{n.omitidas?.length ? <Badge variant="outline" className="ml-1 text-[9px] text-amber-700">{n.omitidas.length} omit.</Badge> : null}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-medium">${CLP(n.monto_total)}</td>
                      <td className="px-3 py-1.5"><EstadoEgreso n={n} /></td>
                      <td className="max-w-[120px] truncate px-3 py-1.5 text-muted-foreground" title={n.generada_por}>{(n.generada_por || '').split('@')[0]}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="h-7 gap-1 px-2" onClick={() => descargar(n)} disabled={bajando === n.id} data-testid={`btn-redescargar-${n.id}`} title="Volver a descargar el archivo TEF">
                          {bajando === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} TEF
                        </Button>
                        {!esCTN && !vigente && (
                          <Button size="sm" variant="outline" className="ml-1 h-7 gap-1 px-2 border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => abrirCrear(n)} data-testid={`btn-crear-egreso-${n.id}`} title="Crear el Comprobante de Egreso en Alegra por el total de esta nómina">
                            <Landmark className="h-3.5 w-3.5" /> Egreso Alegra
                          </Button>
                        )}
                        {vigente && (
                          <Button size="sm" variant="ghost" className="ml-1 h-7 gap-1 px-2 text-red-700 hover:bg-red-50" onClick={() => setAnularPara(n)} data-testid={`btn-anular-egreso-${n.id}`} title="Anular el egreso en Alegra (TEF descartado o regenerado)">
                            <Ban className="h-3.5 w-3.5" /> Anular
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!crearPara} onOpenChange={(o) => !o && setCrearPara(null)}>
          <DialogContent className="max-w-xl" data-testid="dialog-crear-egreso-alegra">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Landmark className="h-5 w-5 text-sky-700" /> Comprobante de Egreso en Alegra</DialogTitle>
              <DialogDescription>{crearPara?.motivo} · {crearPara?.num_transacciones} transferencias · <b>${CLP(crearPara?.monto_total)}</b> · contacto Sueldos</DialogDescription>
            </DialogHeader>
            <EgresoAlegraNominaFields open={!!crearPara} value={egresoCfg} onChange={setEgresoCfg} total={crearPara?.monto_total} testidPrefix="hist-egreso-alegra" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCrearPara(null)} disabled={procesando}>Cancelar</Button>
              <Button onClick={crear} disabled={procesando || !egresoCfg.cuenta_id || !egresoCfg.fecha} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-confirmar-crear-egreso">
                {procesando && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Crear en Alegra
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!anularPara} onOpenChange={(o) => !o && setAnularPara(null)}>
          <AlertDialogContent data-testid="dialog-anular-egreso-alegra">
            <AlertDialogHeader>
              <AlertDialogTitle>Anular egreso N°{anularPara?.egreso_alegra?.number || anularPara?.egreso_alegra?.payment_id} en Alegra</AlertDialogTitle>
              <AlertDialogDescription>
                Úsalo solo si este TEF se descartó o se volvió a generar. El pago queda anulado en Alegra (no se elimina) y el espejo del ERP deja de participar en la conciliación. Si ya está conciliado con la cartola, primero deshaz esa conciliación.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={procesando}>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); anular(); }} disabled={procesando} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-anular-egreso">
                {procesando && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Anular en Alegra
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
